import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';

interface DealerWarningPopupProps {
  onClose: () => void;
}

const DealerWarningPopup: React.FC<DealerWarningPopupProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenWidth < 768;

  const handleAddDealer = () => {
    onClose();
    navigate('/profile');
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: isMobile ? '16px' : '24px',
        animation: 'fadeIn 0.3s ease-out'
      }}
      onClick={onClose}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              transform: translateY(20px);
              opacity: 0;
            }
            to { 
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
          borderRadius: isMobile ? '20px' : '24px',
          padding: isMobile ? '20px' : '28px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '480px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          animation: 'slideUp 0.3s ease-out',
          border: '1px solid rgba(238, 66, 56, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: isMobile ? '12px' : '16px',
            right: isMobile ? '12px' : '16px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'all 0.2s ease',
            color: '#6b7280'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.color = '#ee4238';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          <XMarkIcon style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }} />
        </button>

        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '16px' : '20px' }}>
          <div
            style={{
              width: isMobile ? '64px' : '80px',
              height: isMobile ? '64px' : '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 4px 12px rgba(238, 66, 56, 0.2)'
            }}
          >
            <BuildingStorefrontIcon
              style={{
                width: isMobile ? '32px' : '40px',
                height: isMobile ? '32px' : '40px',
                color: '#ee4238'
              }}
            />
          </div>
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '800',
            color: '#111827',
            textAlign: 'center',
            marginBottom: isMobile ? '12px' : '16px',
            lineHeight: '1.3'
          }}
        >
          Add Your Dealer
        </h2>

        {/* Message */}
        <p
          style={{
            fontSize: isMobile ? '14px' : '16px',
            color: '#4b5563',
            textAlign: 'center',
            marginBottom: isMobile ? '20px' : '24px',
            lineHeight: '1.6',
            padding: `0 ${isMobile ? '8px' : '12px'}`
          }}
        >
          To redeem points and withdraw rewards, please add your dealer code in your Profile settings.
        </p>

        {/* Action Button */}
        <button
          onClick={handleAddDealer}
          style={{
            width: '100%',
            padding: isMobile ? '14px 20px' : '16px 24px',
            fontSize: isMobile ? '15px' : '16px',
            fontWeight: '700',
            color: 'white',
            background: 'linear-gradient(135deg, #ee4238 0%, #d63a32 100%)',
            border: 'none',
            borderRadius: isMobile ? '12px' : '14px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(238, 66, 56, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(238, 66, 56, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(238, 66, 56, 0.3)';
          }}
        >
          <BuildingStorefrontIcon style={{ width: '20px', height: '20px' }} />
          <span>Go to Profile</span>
        </button>

        {/* Skip Link */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: isMobile ? '12px' : '16px',
            padding: isMobile ? '10px' : '12px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            color: '#6b7280',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ee4238';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
};

export default DealerWarningPopup;

